import { Avatar, Form, Modal, Slider, Upload } from '@ui';
import { useAppSelector } from '@hooks/app/redux';
import React from 'react';
import { useTheme } from 'styled-components';
import { EditOutlined } from '@icon';
import AvatarEditor from 'react-avatar-editor';
import { UploadChangeParam, UploadFile } from '@components/ui/upload/upload.types';
import { uploadAvatar } from '@services/external/auth.api';
import { useDispatch } from 'react-redux';
import { actions } from '@store/ducks/users';
import { showNotification } from '@helpers/notification-helper';

const AvatarUploader: React.FunctionComponent = () => {
  const theme = useTheme();
  const user = useAppSelector((state) => state.users.currentUser)!;

  const [imgSrc, setImgSrc] = React.useState('');
  const innerRef = React.useRef<AvatarEditor | null>(null);
  const { open, close, modalProps } = Modal.useModal();
  const [isLoading, setIsLoading] = React.useState(false);
  const dispatch = useDispatch();

  const onSelectFile = React.useCallback((e: UploadChangeParam<UploadFile<any>>) => {
    const files = Upload.getValueFromEvent(e);

    if (files && files.length > 0) {
      const reader = new FileReader();

      reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
      reader.readAsDataURL(files[0]?.originFileObj as File);
    }
  }, []);

  const [form, formProps] = Form.useForm({
    initialValues: { zoom: 1, rotation: 0 },
    onSubmit: async () => {
      if (!innerRef.current) {
        return;
      }
      setIsLoading(true);

      const imgBase64 = innerRef.current.getImageScaledToCanvas();

      const blob = await new Promise((res) => {
        imgBase64.toBlob((b) => res(b), undefined, 0.2);
      });

      try {
        const formData = new FormData();
        formData.append('avatar', blob as Blob);
        await uploadAvatar(formData);

        dispatch(actions.getCurrentUserRequest());
        close();
      } catch (error) {
        dispatch(actions.getCurrentUserRequest());
        showNotification({
          type: 'ERROR',
          title: 'Niečo sa pokazilo',
          copy: '',
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  React.useLayoutEffect(() => {
    form?.resetForm?.();
    setImgSrc('');
  }, [modalProps.visible]);

  const isImgSelected = !!imgSrc;
  const disabled = !isImgSelected;
  return (
    <>
      <Avatar
        size={'Xlarge'}
        style={{ marginBottom: theme.spacings.component.M, display: 'block' }}
        action={<Avatar.Action Icon={EditOutlined} onClick={open} />}
        src={user?.profile.profileImage}
        isExpert
      />
      <Modal
        title="Avatar"
        {...modalProps}
        onClose={close}
        destroyOnClose
        footer={
          <Modal.Actions
            actions={[
              { key: 'cancel', label: 'Zrušiť', variant: 'ghost', onClick: close },
              {
                key: 'submit',
                label: 'Nahrať',
                variant: 'primary',
                onClick: () => form?.submitForm(),
                disabled,
                loading: isLoading,
              },
            ]}
          />
        }
      >
        <Form {...formProps} showFeedback={false} disabled={disabled}>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            {isImgSelected ? (
              <Form.Custom>
                {(f) => (
                  <AvatarEditor
                    image={imgSrc}
                    width={200}
                    height={200}
                    border={10}
                    color={[0, 0, 0, 0.6]}
                    rotate={f.values.rotation}
                    ref={innerRef}
                    borderRadius={100}
                    scale={f.values.zoom}
                    style={{ marginTop: 8, marginBottom: 8 }}
                    backgroundColor={theme.color.surface.baseline}
                  />
                )}
              </Form.Custom>
            ) : (
              <Form.Field
                name="file"
                showLabel={false}
                style={{ width: 'fit-content' }}
                getValueFromEvent={Upload.getValueFromEvent}
              >
                <Upload.Dragger
                  accept="image/jpeg,image/png"
                  beforeUpload={() => false}
                  showFileList={false}
                  maxCount={1}
                  multiple={false}
                  style={{ width: 220, height: 220 }}
                  onChange={onSelectFile}
                  disabled={false}
                >
                  <Upload.Dragger.Button
                    style={{ width: '100%', height: '100%' }}
                    description={'.jpeg, .png'}
                  />
                </Upload.Dragger>
              </Form.Field>
            )}
            <div style={{ width: 220 }}>
              <Form.Field name="zoom" getValueFromEvent={(e) => e} label="zoom" required>
                <Slider min={0.2} max={5} step={0.1} />
              </Form.Field>
              <Form.Field name="rotation" getValueFromEvent={(e) => e} label="rotation" required>
                <Slider min={-360} max={360} />
              </Form.Field>
            </div>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default AvatarUploader;
